import { useId, useState } from "react";
import { HiOutlineClock, HiOutlinePencilSquare } from "react-icons/hi2";

import type { DocumentWorkflowFile } from "../api/generated";
import { useDocumentFiles } from "./documentFilesContext";
import { ScanStatusBadge, SignatureStateBadge } from "./FileBadges";
import { displayDateTime, scanStatusView } from "./fileLabels";
import { FileProvenance } from "./FileProvenance";
import { FileTypeIcon } from "./FileTypeIcon";
import { FileVersionHistory } from "./FileVersionHistory";
import { fileTypeLabel, formatBytes } from "./formatting";
import { NewVersionUpload } from "./NewVersionUpload";
import { VersionControls } from "./VersionControls";

/**
 * Логический файл документа: актуальная версия и всё, что с ней можно делать.
 *
 * История версий раскрывается по требованию: у каждой версии свои автор, хеш,
 * состояние проверки, источник и подписи.
 */
export function DocumentFileRow({
  file,
  onDelete,
  onRename,
  deleting,
}: {
  file: DocumentWorkflowFile;
  onDelete: (file: DocumentWorkflowFile) => void;
  onRename: (file: DocumentWorkflowFile) => void;
  deleting: boolean;
}) {
  const { canUpload, authorName } = useDocumentFiles();
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyId = useId();
  const version = file.current_version;
  const signed = version?.signature_state === "signed";

  return (
    <li className="file-card" data-source={file.provenance.type}>
      <div className="file-card__head">
        <FileTypeIcon mimeType={version?.detected_mime ?? null} />
        <div className="file-card__title">
          <strong className="file-card__name">
            {file.display_name}
            <span className="file-card__meta">
              {version
                ? `${fileTypeLabel(version.detected_mime)} · ${formatBytes(version.size ?? 0)} · ${displayDateTime(version.created_at)}`
                : "версия отсутствует"}
            </span>
          </strong>
        </div>
        {canUpload ? (
          <button
            className="button button--text button--compact file-rename-control"
            type="button"
            title="Переименовать файл"
            aria-label="Переименовать файл"
            onClick={() => onRename(file)}
          >
            <HiOutlinePencilSquare aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="file-card__badges">
        <ScanStatusBadge status={file.scan_status} />
        {version ? (
          <SignatureStateBadge state={version.signature_state} />
        ) : null}
        {version ? (
          <span className="file-card__state">
            Версия {version.version_number} из {file.version_count}
          </span>
        ) : null}
      </div>
      {version ? (
        <FileProvenance
          provenance={file.provenance}
          referenceCount={version.reference_count}
          authorName={authorName}
        />
      ) : null}
      {version ? (
        // Ключ по версии: сведения и открытое подписание не переезжают на новую
        // актуальную версию, пока человек смотрит на прежнюю.
        <VersionControls
          key={version.id}
          version={version}
          displayName={file.display_name}
        />
      ) : (
        <p className="file-hint">
          {scanStatusView(file.scan_status).blockedReason}
        </p>
      )}
      <div className="file-actions file-actions--secondary">
        <button
          className="button button--text button--compact"
          type="button"
          aria-expanded={historyOpen}
          aria-controls={historyId}
          onClick={() => setHistoryOpen((open) => !open)}
        >
          <HiOutlineClock aria-hidden="true" />
          <span>{`История версий (${file.version_count})`}</span>
        </button>
        {canUpload && version ? <NewVersionUpload file={file} /> : null}
        {file.can_delete && !signed ? (
          <button
            className="button button--text button--compact file-actions__delete"
            type="button"
            disabled={deleting}
            onClick={() => onDelete(file)}
          >
            Удалить
          </button>
        ) : null}
      </div>
      {historyOpen ? (
        <div id={historyId} className="file-panel">
          <FileVersionHistory
            fileId={file.id}
            displayName={file.display_name}
            currentVersionId={version?.id ?? null}
          />
        </div>
      ) : null}
    </li>
  );
}
